import moment from "moment";
import React, { useState, useEffect, useRef } from "react";

import { NetworkStatus, useReactiveVar } from "@apollo/client";
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import uniqBy from "lodash/uniqBy";

import {
  GetEventsPendingHumanValidationQuery,
  GetEventsPendingHumanValidationByTenantQuery,
  ModelType,
  ValidationState,
} from "../../../API";
import StatusToggle from "../../../common/components/item/StatusToggle";
import CustomerSelect from "../../../common/components/layout/components/CustomerSelect";
import {
  useGetHumanValidatorByTenantTableData,
  type PendingEventRow,
} from "../hooks/useHumanValidatorByTenantTableData";
import { useTakeToValidate } from "../hooks/useTakeToValidate";
import { humanValidatorTableVariable } from "../variables/humanValidatorTable";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { handleApolloError } from "../../../common/utils/handleApolloError";
import { ApolloError } from "@apollo/client";
import { HV_PAGE_SIZE } from "../../../common/variables/common";

const CCAdminHumanValidatorTable = (): JSX.Element => {
  const customerId = useCustomerIdGuard();

  const { tableData, showDetails } = useReactiveVar(
    humanValidatorTableVariable
  );
  const [useCustomerSelectValue, setUseCustomerSelectValue] = useState(false);

  const { rows, nextToken, loading, networkStatus, fetchMore } =
    useGetHumanValidatorByTenantTableData(useCustomerSelectValue);

  const { takeToValidate } = useTakeToValidate();

  const apiMutationTakeEventToValidate = (
    row: PendingEventRow,
    customerId: string,
    eventId: string
  ): void => {
    takeToValidate({
      variables: {
        customerId,
        eventId,
      },
    })
      .then((response): void => {
        humanValidatorTableVariable({
          tableData: {
            ...row,
            ...response.data?.takeEventToValidate,
          },
          showDetails: true,
        });
      })
      .catch((error): void => {
        console.error(error);
      });
  };

  const handleRowClick = (
    _event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: PendingEventRow
  ): void => {
    if (showDetails) {
      return;
    }

    apiMutationTakeEventToValidate(row, row.customerId, row.eventId);
  };

  const networkStatusChanged = [NetworkStatus.fetchMore].includes(
    networkStatus
  );

  const handleFetchMore = async () => {
    if (!nextToken || loading || networkStatus === NetworkStatus.fetchMore)
      return;

    try {
      if (useCustomerSelectValue) {
        await fetchMore({
          variables: {
            tenantId: customerId,
            nextToken,
            limit: HV_PAGE_SIZE,
          },
          updateQuery: (
            prev: GetEventsPendingHumanValidationByTenantQuery,
            {
              fetchMoreResult,
            }: {
              fetchMoreResult?: GetEventsPendingHumanValidationByTenantQuery;
            }
          ): GetEventsPendingHumanValidationByTenantQuery => {
            const prevRoot = prev.getEventsPendingHumanValidationByTenant ?? {
              __typename: "PaginatedHumanValidationEvents",
              items: [],
              nextToken: null,
            };
            const incoming =
              fetchMoreResult?.getEventsPendingHumanValidationByTenant;

            if (!incoming?.items) {
              return { getEventsPendingHumanValidationByTenant: prevRoot };
            }

            const merged = uniqBy(
              [...(prevRoot.items ?? []), ...(incoming.items ?? [])],
              item => item?.id
            );

            return {
              getEventsPendingHumanValidationByTenant: {
                ...prevRoot,
                items: merged,
                nextToken: incoming.nextToken ?? null,
              },
            };
          },
        });
      } else {
        await fetchMore({
          variables: {
            nextToken,
            limit: HV_PAGE_SIZE,
          },
          updateQuery: (
            prev: GetEventsPendingHumanValidationQuery,
            {
              fetchMoreResult,
            }: { fetchMoreResult?: GetEventsPendingHumanValidationQuery }
          ): GetEventsPendingHumanValidationQuery => {
            const prevRoot = prev.getEventsPendingHumanValidation ?? {
              __typename: "PaginatedHumanValidationEvents",
              items: [],
              nextToken: null,
            };
            const incoming = fetchMoreResult?.getEventsPendingHumanValidation;

            if (!incoming?.items) {
              return { getEventsPendingHumanValidation: prevRoot };
            }

            const merged = uniqBy(
              [...(prevRoot.items ?? []), ...(incoming.items ?? [])],
              item => item?.id
            );

            return {
              getEventsPendingHumanValidation: {
                ...prevRoot,
                items: merged,
                nextToken: incoming.nextToken ?? null,
              },
            };
          },
        });
      }
    } catch (error) {
      handleApolloError(error as ApolloError);
    }
  };

  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!nextToken || loading || networkStatus === NetworkStatus.fetchMore)
      return;

    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;

    handleFetchMore();
  }, [nextToken, loading, networkStatus, handleFetchMore]);

  useEffect(() => {
    if (networkStatus !== NetworkStatus.fetchMore)
      loadingMoreRef.current = false;
  }, [networkStatus]);

  return (
    <>
      <Box sx={{ marginBottom: "1.5em" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            padding: "1em",
            backgroundColor: theme => theme.palette.otherBackgroundLight.main,
            border: theme =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
            borderRadius: "12px",
          }}
        >
          <Box sx={{ minWidth: "240px" }}>
            <CustomerSelect />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2Regular"
              sx={{ color: theme => theme.palette.text.secondary }}
            >
              Use Customer Select
            </Typography>
            <StatusToggle
              checked={useCustomerSelectValue}
              handleChange={(event): void => {
                setUseCustomerSelectValue(event.target.checked);
              }}
            />
          </Stack>
        </Stack>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Event ID</TableCell>
            <TableCell align="right">Date</TableCell>
            <TableCell align="right">Model</TableCell>
            <TableCell align="right">Customer</TableCell>
            <TableCell align="right">Location</TableCell>
            <TableCell align="right">Camera</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {React.Children.toArray(
            rows.map((row): JSX.Element => {
              const fromUTCToLocalDate = moment
                .utc(row.date)
                .local()
                .format("YYYY-MM-DD HH:mm");

              const date = row.date ? fromUTCToLocalDate : "n/a";

              const renameGL =
                row.model === ModelType.gasleak ? "leak" : row.model;

              return (
                <React.Fragment key={row.rowId}>
                  <TableRow
                    key={row.rowId}
                    hover
                    selected={row.rowId === tableData?.rowId}
                    onClick={(event): void => handleRowClick(event, row)}
                    sx={{
                      cursor:
                        showDetails ||
                        row.validationState === ValidationState.IN_VALIDATION
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <TableCell>{row.rowId}</TableCell>
                    <TableCell align="right">{date}</TableCell>
                    <TableCell align="right">{renameGL}</TableCell>
                    <TableCell align="right" sx={{ wordBreak: "break-all" }}>
                      {row.customerId.split("#").pop()}
                    </TableCell>
                    <TableCell align="right" sx={{ wordBreak: "break-all" }}>
                      {row.locationId.split("#").pop()}
                    </TableCell>
                    <TableCell align="right">{row.cameraName}</TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}

          {nextToken && (
            <TableRow key={`sentinel-${nextToken}`}>
              <TableCell colSpan={6}></TableCell>
            </TableRow>
          )}

          {(loading || networkStatusChanged) && (
            <TableRow>
              <TableCell colSpan={6}>
                {[...Array(6)].map(
                  (_, index): JSX.Element => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{ my: 3 }}
                    />
                  )
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default CCAdminHumanValidatorTable;
